'use client';

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { validateFileSize, MAX_FILE_SIZE_MB, validateFileType } from '../../../../../utils/validationUtils';

export default function SellerStep5Documents() {
  const { register, watch, setValue, formState: { errors, touchedFields, isSubmitted } } = useFormContext();
  const { translate, language } = useLanguage();
  const isRTL = language === 'ar';
  const [previewFront, setPreviewFront] = useState('');
  const [previewBack, setPreviewBack] = useState('');
  const [fileSizeError, setFileSizeError] = useState({ front: '', back: '' });

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];

    if (file) {
      // Validate file type
      if (!validateFileType(file)) {
        const errorMsg = translate('auth.signup.fileFormats');
        setFileSizeError(prev => ({ ...prev, [type]: errorMsg }));
        e.target.value = '';
        if (type === 'front') {
          setPreviewFront('');
        } else {
          setPreviewBack('');
        }
        return;
      }

      // Validate file size (5MB limit)
      if (!validateFileSize(file)) {
        const errorMsg = translate('auth.signup.validation.fileSizeError') || `File size must not exceed ${MAX_FILE_SIZE_MB}MB`;
        setFileSizeError(prev => ({ ...prev, [type]: errorMsg }));
        // Clear the file input
        e.target.value = '';
        if (type === 'front') {
          setPreviewFront('');
        } else {
          setPreviewBack('');
        }
        return;
      }

      // Clear any previous error
      setFileSizeError(prev => ({ ...prev, [type]: '' }));

      const reader = new FileReader();

      reader.onloadend = () => {
        if (type === 'front') {
          setPreviewFront(reader.result);
        } else {
          setPreviewBack(reader.result);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (type) => {
    if (type === 'front') {
      setValue('idFront', null);
      setPreviewFront('');
      setFileSizeError(prev => ({ ...prev, front: '' }));
      const input = document.getElementById('idFront');
      if (input) input.value = '';
    } else {
      setValue('idBack', null);
      setPreviewBack('');
      setFileSizeError(prev => ({ ...prev, back: '' }));
      const input = document.getElementById('idBack');
      if (input) input.value = '';
    }
  };


  return (
    <div className="space-y-6">
      <div className='text-center'>
        <h2 className="text-2xl font-boldtext-slate-700 mb-2">
          {translate("auth.signup.requiredDocuments")}
        </h2>
        <p className="text-white">
          {translate("auth.signup.uploadClearPhotos")}
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <label htmlFor="idFront" className="block text-sm font-medium text-white mb-2">
            {translate("auth.signup.emiratesIdFront")} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('idFront')}
              id="idFront"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                handleFileChange(e, 'front');
                register('idFront').onChange?.(e);
              }}
            />
            <label
              htmlFor="idFront"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer "
            >
              <span className="text-gray-500 truncate mr-2">
                {watch('idFront')?.[0]?.name || translate("auth.signup.uploadClearPhotos")}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {watch('idFront')?.[0] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveFile('front');
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <span className="bg-primary text-white px-3 py-1 rounded text-sm shrink-0">
                  {translate("auth.signup.browseFile")}
                </span>
              </div>
            </label>
          </div>
          {(touchedFields.idFront || isSubmitted) && !fileSizeError.front && errors.idFront && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.idFront.message)}
            </p>
          )}
          {fileSizeError.front && (
            <p className="text-red-500 text-sm mt-1">
              {fileSizeError.front}
            </p>
          )}

          {previewFront && (
            <div className="mt-3 border-2 border-gray-200 rounded-lg overflow-hidden">
              <img
                src={previewFront || "/placeholder.svg"}
                alt={translate("auth.signup.idFrontPreview")}
                className="w-full h-auto max-h-48 object-cover"
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label htmlFor="idBack" className="block text-sm font-mediumtext-whitemb-2">
            {translate("auth.signup.emiratesIdBack")}
          </label>
          <div className="relative">
            <input
              {...register('idBack')}
              id="idBack"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                handleFileChange(e, 'back');
                register('idBack').onChange?.(e);
              }}
            />
            <label
              htmlFor="idBack"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer "
            >
              <span className="text-gray-500 truncate mr-2">
                {watch('idBack')?.[0]?.name || translate("auth.signup.uploadClearPhotos")}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {watch('idBack')?.[0] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveFile('back');
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <span className="bg-primary text-white px-3 py-1 rounded text-sm shrink-0">
                  {translate("auth.signup.browseFile")}
                </span>
              </div>
            </label>
          </div>
          {(touchedFields.idBack || isSubmitted) && !fileSizeError.back && errors.idBack && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.idBack.message)}
            </p>
          )}
          {fileSizeError.back && (
            <p className="text-red-500 text-sm mt-1">
              {fileSizeError.back}
            </p>
          )}

          {previewBack && (
            <div className="mt-3 border-2 border-gray-200 rounded-lg overflow-hidden">
              <img
                src={previewBack || "/placeholder.svg"}
                alt={translate("auth.signup.idBackPreview")}
                className="w-full h-auto max-h-48 object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
